import { t } from '../../i18n';
import { getGrid } from '../../grid';
import { drawGridClippedPathTileImage } from '../../renderer/tilemap/pathTileRenderGeometry';
import { createWindowFrame, windowId } from '../windowing';
import { composeHexPathBrushPreview } from '../../terrain/pathAutotiler/hexPathProcedural';
import { composeSquarePathBrushPreview } from '../../terrain/pathAutotiler/squarePathProcedural';
import { composeTrianglePathBrushPreview, getTrianglePathBrushLabel } from '../../terrain/pathAutotiler/trianglePathProcedural';
import type { PathTileAtlasId } from '../../terrain/pathArtStyles';
import { getAllVisualAssetDefinitions, getVisualAssetDefinition, replaceRuntimeVisualAssets } from '../../visualAssets/catalog';
import { PATH_TILE_ATLASES, getPathTileAtlasDefinition, getPathTileBrushCount, getPathTileTextureSync } from '../../terrain/pathTileRegistry';
import { TERRAIN_BIOME_DEFINITIONS, getTerrainBiomeDefinition, getTerrainTexturePath } from '../../terrain/registry';
import { createAdminInput, createMapSelect, createSectionLabel, buttonStyle, stopEditingEventPropagation } from './adminUtilities/common';
import { renderAdminConsoleTab } from './adminUtilities/renderAdminConsoleTab';
import { fileToLocalVisualAsset, isLocalVisualAssetId, loadLocalVisualAssets, saveLocalVisualAssets } from './localVisualAssets';
import type {
  AdminBiomeBrush,
  AdminTab,
  AdminTerrainBrush,
  AdminTileEditorTab,
  AdminToolSelection,
  AdminWindowHandle,
  AdminWindowOptions,
} from './types';

const NPC_TYPES = ['merchant', 'guard', 'villager', 'town_wall_v', 'town_wall_h', 'town_gate', 'shrine'] as const;
const TABS: AdminTab[] = ['map-editor', 'console'];
const TAB_LABELS: Record<AdminTab, string> = {
  'map-editor': 'Map Editor',
  console: 'Admin Console',
};
const TILE_EDITOR_TABS: AdminTileEditorTab[] = ['collision', 'terrain', 'paths', 'npcs', 'assets', 'portals'];
const TILE_EDITOR_TAB_LABELS: Record<AdminTileEditorTab, string> = {
  collision: 'Collision',
  terrain: 'Terrain',
  paths: 'Paths',
  npcs: 'NPCs',
  assets: 'Assets',
  portals: 'Portals',
};
const VISUAL_ASSET_DRAG_MIME = 'application/x-mmo-visual-asset';
const MAP_OBJECT_DRAG_STARTED_EVENT = 'map-object-drag-started';
const MAP_OBJECT_DRAG_ENDED_EVENT = 'map-object-drag-ended';
const EMPTY_DRAG_IMAGE = (() => {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas;
})();

function createClippedPathBrushPreview(
  source: CanvasImageSource,
  gridType: 'square' | 'hex' | 'triangle',
  tileSize: number,
  drawScale = 1,
): HTMLCanvasElement {
  if (gridType === 'square') {
    const canvas = document.createElement('canvas');
    canvas.width = tileSize;
    canvas.height = tileSize;
    const context = canvas.getContext('2d');
    if (!context) {
      return canvas;
    }
    context.imageSmoothingEnabled = false;
    context.drawImage(source, 0, 0, tileSize, tileSize);
    return canvas;
  }

  const canvas = document.createElement('canvas');
  canvas.width = tileSize;
  canvas.height = tileSize;

  const context = canvas.getContext('2d');
  if (!context) {
    return canvas;
  }

  context.imageSmoothingEnabled = false;
  const drawSize = gridType === 'hex' ? tileSize * drawScale : tileSize;
  drawGridClippedPathTileImage({
    context,
    grid: getGrid(gridType),
    gridType,
    tileSize,
    tileX: 0,
    tileY: 0,
    boxCenterX: tileSize / 2,
    boxCenterY: tileSize / 2,
    drawWidth: drawSize,
    drawHeight: drawSize,
    image: source,
    rotationSteps: 0,
    flipHorizontal: false,
    flipVertical: false,
  });

  return canvas;
}

type PathBrushPreviewComposer = (
  atlasId: PathTileAtlasId,
  tileSize: number,
  tileIndex: number,
  textureImage?: HTMLImageElement,
) => HTMLCanvasElement;

const PATH_BRUSH_PREVIEW_BY_GRID: Record<
  'square' | 'hex' | 'triangle',
  {
    readonly compose: PathBrushPreviewComposer;
    readonly clippedGridType: 'square' | 'hex' | 'triangle';
  }
> = {
  square: {
    compose: composeSquarePathBrushPreview,
    clippedGridType: 'square',
  },
  hex: {
    compose: composeHexPathBrushPreview,
    clippedGridType: 'hex',
  },
  triangle: {
    compose: composeTrianglePathBrushPreview,
    clippedGridType: 'triangle',
  },
};

function createPathBrushPreviewNode(
  atlasId: PathTileAtlasId,
  gridType: 'square' | 'hex' | 'triangle',
  tileSize: number,
  tileIndex: number,
  drawScale = 1,
): HTMLCanvasElement {
  const previewConfig = PATH_BRUSH_PREVIEW_BY_GRID[gridType];
  const source = previewConfig.compose(atlasId, tileSize, tileIndex, getPathTileTextureSync(atlasId, gridType));
  return createClippedPathBrushPreview(source, previewConfig.clippedGridType, tileSize, drawScale);
}

export function createAdminWindow(options: AdminWindowOptions): AdminWindowHandle {
  const {
    player,
    getIsSuperAdmin,
    onToolSelect,
    onWipeAndReloadMaps,
    onUploadMapJson,
    onSaveFrontendMapAsset,
    onSetMapDefaultBiome,
    loadMapCatalog,
    createMap,
    deleteMap,
    getCurrentMapId,
    getCurrentMapGridShape,
    scanPlayersOnMap,
    disconnectPlayerSession,
    bringPlayerHere,
  } = options;

  let isOpen = false;
  let activeTab: AdminTab = 'map-editor';
  let activeTileEditorTab: AdminTileEditorTab = 'collision';
  let selectedTool: AdminToolSelection | null = null;
  let playerQuery = '';
  let searchStatus: string | null = null;
  let isSearching = false;
  let isDisconnecting = false;
  let isBringing = false;
  let isReloadingMaps = false;
  let isSavingFrontendMapAsset = false;
  let isUploadingMapJson = false;
  let isSettingDefaultBiome = false;
  let tileEditorStatus = 'No admin brush selected.';
  let mapCatalog: import('../../spacetime/SpaceTimeClient').AdminMapSummary[] = [];
  let isLoadingMapCatalog = false;
  let mapCatalogStatus = '';
  let createMapId = '';
  let createMapName = '';
  let createMapWidth = '8';
  let createMapHeight = '8';
  let createMapGridType: import('./types').AdminGridShape = 'hex';
  let deleteMapId = '';
  let portalDestMapId = '';
  let portalDestX = '0';
  let portalDestY = '0';
  let portalAutoMode = false;
  let activePathAtlasId: import('../../terrain/pathArtStyles').PathTileAtlasId = 'dirt';
  let tileEditorSearchInput = '';
  let tileEditorSearchQuery = '';
  let tileEditorSearchTimeoutId: number | null = null;
  let tileEditorSearchField: HTMLInputElement | null = null;
  let localVisualAssets = loadLocalVisualAssets();
  replaceRuntimeVisualAssets(localVisualAssets);

  const initialSize = {
    width: Math.max(360, Math.round(window.innerWidth * 0.32)),
    height: Math.max(420, Math.round(window.innerHeight * 0.58)),
  };
  const initialPosition = {
    x: Math.max(24, Math.round(window.innerWidth - initialSize.width - 32)),
    y: 72,
  };

  const container = document.createElement('div');
  container.className = 'window-container admin-window';
  Object.assign(container.style, {
    position: 'fixed',
    flexDirection: 'column',
    left: `${initialPosition.x}px`,
    top: `${initialPosition.y}px`,
    width: `${initialSize.width}px`,
    height: `${initialSize.height}px`,
    minHeight: '0',
    overflow: 'hidden',
    display: 'none',
  });

  const header = document.createElement('div');
  header.className = 'window-header';

  const headerTitle = document.createElement('div');
  headerTitle.className = 'window-header-title';
  headerTitle.textContent = t('admin.headerTitle');

  const resizeHandle = document.createElement('div');
  resizeHandle.className = 'window-resize-handle';
  resizeHandle.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" style="pointer-events: none;"><path d="M0 0 L10 0 L10 10 Z" fill="#9ca3af" /></svg>';

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.textContent = '×';
  Object.assign(closeBtn.style, {
    marginLeft: 'auto',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#9ca3af',
    fontSize: '1.5rem',
    cursor: 'pointer',
    lineHeight: '1',
    zIndex: '20',
  });
  closeBtn.addEventListener('click', (event) => {
    event.stopPropagation();
    hide();
  });

  header.appendChild(headerTitle);
  header.appendChild(resizeHandle);
  header.appendChild(closeBtn);

  const topTabBar = document.createElement('div');
  topTabBar.className = 'chat-box-tabs';
  Object.assign(topTabBar.style, {
    display: 'flex',
    flex: '0 0 auto',
    background: 'rgba(0, 0, 0, 0.2)',
    borderBottom: '1px solid #2a3142',
  });

  const tabButtons = new Map<AdminTab, HTMLButtonElement>();
  TABS.forEach((tab) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = TAB_LABELS[tab];
    Object.assign(button.style, {
      flex: '1',
      padding: '10px 12px',
      border: 'none',
      borderBottom: '2px solid transparent',
      background: 'transparent',
      color: '#cbd5e1',
      cursor: 'pointer',
      fontWeight: '600',
    });
    button.addEventListener('click', () => setActiveTab(tab));
    tabButtons.set(tab, button);
    topTabBar.appendChild(button);
  });

  const content = document.createElement('div');
  content.className = 'window-content';
  Object.assign(content.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    flex: '1 1 auto',
    minHeight: '0',
    overflowY: 'auto',
  });

  container.appendChild(header);
  container.appendChild(topTabBar);
  container.appendChild(content);
  stopEditingEventPropagation(container);
  document.body.appendChild(container);

  const windowFrame = createWindowFrame({
    windowId: windowId('admin-window', 'main'),
    container,
    header,
    resizeHandle,
    closeButton: closeBtn,
    minWidth: 320,
    minHeight: 300,
    initialPosition,
    initialSize,
  });

  const restoredTab = windowFrame.getActiveTab();
  if (TABS.includes(restoredTab as AdminTab)) {
    activeTab = restoredTab as AdminTab;
  } else {
    windowFrame.setActiveTab(activeTab);
  }

  if (windowFrame.getWindowState() === 'open') {
    isOpen = true;
    container.style.display = 'flex';
  }

  updateTabStyles();
  render();
  if (activeTab === 'console') {
    void ensureConsoleMapCatalogLoaded();
  }

  function show(): void {
    if (isOpen) return;
    isOpen = true;
    container.style.display = 'flex';
    windowFrame.setWindowState('open');
    render();
    if (activeTab === 'console') {
      void ensureConsoleMapCatalogLoaded();
    }
  }

  function hide(): void {
    if (!isOpen) return;
    isOpen = false;
    container.style.display = 'none';
    windowFrame.setWindowState('closed');
  }

  function setTileEditorSearchInput(value: string): void {
    tileEditorSearchInput = value;
    if (tileEditorSearchTimeoutId !== null) {
      window.clearTimeout(tileEditorSearchTimeoutId);
    }
    tileEditorSearchTimeoutId = window.setTimeout(() => {
      tileEditorSearchQuery = tileEditorSearchInput;
      tileEditorSearchTimeoutId = null;
      if (isOpen && activeTab === 'map-editor') {
        render();
      }
    }, 300);
  }

  function setActiveTab(tab: AdminTab): void {
    activeTab = tab;
    windowFrame.setActiveTab(tab);
    updateTabStyles();
    if (tab === 'console') {
      void ensureConsoleMapCatalogLoaded();
    }
    if (isOpen) {
      render();
    }
  }

  function setActiveTileEditorTab(tab: AdminTileEditorTab): void {
    activeTileEditorTab = tab;
    if (tab === 'portals' && mapCatalog.length === 0 && !isLoadingMapCatalog) {
      void refreshMapCatalog();
    }
    if (isOpen) {
      render();
    }
  }

  function setSelectedTool(tool: AdminToolSelection | null): void {
    selectedTool = tool;
    onToolSelect(tool);
    updateTileEditorStatusForTool(tool);
    if (isOpen) {
      render();
    }
  }

  function updateTabStyles(): void {
    tabButtons.forEach((button, tab) => {
      const selected = tab === activeTab;
      button.style.color = selected ? '#f8fafc' : '#cbd5e1';
      button.style.borderBottomColor = selected ? '#38bdf8' : 'transparent';
      button.style.background = selected ? 'rgba(56, 189, 248, 0.12)' : 'transparent';
    });
  }

  function render(): void {
    const restoreSearchFocus = document.activeElement === tileEditorSearchField;
    const restoreSearchSelectionStart = restoreSearchFocus ? tileEditorSearchField?.selectionStart ?? null : null;
    const restoreSearchSelectionEnd = restoreSearchFocus ? tileEditorSearchField?.selectionEnd ?? null : null;

    tileEditorSearchField = null;
    content.innerHTML = '';
    if (activeTab === 'map-editor') {
      renderTileEditorTab();
      if (restoreSearchFocus && tileEditorSearchField) {
        tileEditorSearchField.focus();
        if (restoreSearchSelectionStart !== null || restoreSearchSelectionEnd !== null) {
          tileEditorSearchField.setSelectionRange(
            restoreSearchSelectionStart ?? tileEditorSearchField.value.length,
            restoreSearchSelectionEnd ?? restoreSearchSelectionStart ?? tileEditorSearchField.value.length,
          );
        }
      }
      return;
    }
    renderAdminConsoleTab(content, {
      isLoadingMapCatalog,
      mapCatalogStatus,
      isSavingFrontendMapAsset,
      isUploadingMapJson,
      isReloadingMaps,
      mapCatalog,
      createMapId,
      createMapName,
      createMapWidth,
      createMapHeight,
      createMapGridType,
      deleteMapId,
      selectedTool,
      currentMapId: getCurrentMapId(),
      currentMapGridShape: getCurrentMapGridShape(),
      playerQuery,
      searchStatus,
      isSearching,
      isDisconnecting,
      isBringing,
    }, {
      render,
      setMapCatalogStatus: (value) => { mapCatalogStatus = value; },
      setSavingFrontendMapAsset: (value) => { isSavingFrontendMapAsset = value; },
      setUploadingMapJson: (value) => { isUploadingMapJson = value; },
      setReloadingMaps: (value) => { isReloadingMaps = value; },
      setCreateMapId: (value) => { createMapId = value; },
      setCreateMapName: (value) => { createMapName = value; },
      setCreateMapWidth: (value) => { createMapWidth = value; },
      setCreateMapHeight: (value) => { createMapHeight = value; },
      setCreateMapGridType: (value) => { createMapGridType = value; },
      setDeleteMapId: (value) => { deleteMapId = value; },
      setSelectedTool,
      setPlayerQuery: (value) => { playerQuery = value; },
      setSearchStatus: (value) => { searchStatus = value; },
      setSearching: (value) => { isSearching = value; },
      setDisconnecting: (value) => { isDisconnecting = value; },
      setBringing: (value) => { isBringing = value; },
      onSaveFrontendMapAsset,
      onWipeAndReloadMaps,
      onUploadMapJson,
      createMap,
      deleteMap,
      refreshMapCatalog,
      scanPlayersOnMap,
      disconnectPlayerSession,
      bringPlayerHere,
    });
  }

  async function ensureConsoleMapCatalogLoaded(): Promise<void> {
    if (isLoadingMapCatalog || mapCatalog.length > 0) {
      return;
    }
    await refreshMapCatalog();
  }

  async function addLocalVisualAssets(files: File[]): Promise<void> {
    try {
      const currentDefinitions = getAllVisualAssetDefinitions();
      const uploaded = await Promise.all(files.map(async (file) => {
        const existing = findExistingAssetForUpload(file.name, currentDefinitions);
        return fileToLocalVisualAsset(file, existing);
      }));
      const uploadedById = new Map(uploaded.map((asset) => [asset.id, asset] as const));
      localVisualAssets = [
        ...localVisualAssets.filter((asset) => !uploadedById.has(asset.id)),
        ...uploaded,
      ];
      replaceRuntimeVisualAssets(localVisualAssets);
      saveLocalVisualAssets(localVisualAssets);
      const replacedCount = uploaded.filter((asset) => !isLocalVisualAssetId(asset.id)).length;
      const createdCount = uploaded.length - replacedCount;
      tileEditorStatus = summarizeAssetUpload(createdCount, replacedCount);
      render();
    } catch (error) {
      tileEditorStatus = error instanceof Error ? error.message : 'Failed to upload local assets.';
      render();
    }
  }

  function removeLocalVisualAsset(assetId: string): void {
    const removed = getVisualAssetDefinition(assetId);
    localVisualAssets = localVisualAssets.filter((asset) => asset.id !== assetId);
    replaceRuntimeVisualAssets(localVisualAssets);
    saveLocalVisualAssets(localVisualAssets);
    if (selectedTool?.kind === 'map-object-place' && selectedTool.assetId === assetId) {
      setSelectedTool(null);
      return;
    }
    tileEditorStatus = `${isLocalVisualAssetId(assetId) ? 'Removed' : 'Reset'} asset '${removed?.label ?? assetId}'.`;
    render();
  }

  function renderTileEditorTab(): void {
    const info = createSectionLabel('Tile Editing');

    const mapLine = document.createElement('div');
    mapLine.textContent = `Editing map: ${getCurrentMapId()}`;
    Object.assign(mapLine.style, { color: '#cbd5e1', fontSize: '13px' });

    const helpLine = document.createElement('div');
    helpLine.textContent = 'Pick a layer tab, choose a brush, then click the world to apply it. Asset brushes can also be dragged onto the map.';
    Object.assign(helpLine.style, { color: '#94a3b8', fontSize: '12px' });

    const statusLine = document.createElement('div');
    statusLine.textContent = tileEditorStatus;
    Object.assign(statusLine.style, {
      color: '#f8fafc',
      fontSize: '12px',
      padding: '8px',
      borderRadius: '6px',
      background: 'rgba(15, 23, 42, 0.75)',
    });

    info.appendChild(mapLine);
    info.appendChild(helpLine);
    info.appendChild(statusLine);
    content.appendChild(info);

    content.appendChild(createTileEditorSubtabBar(activeTileEditorTab, setActiveTileEditorTab));
    const searchGroup = document.createElement('div');
    Object.assign(searchGroup.style, {
      display: 'flex',
      flexDirection: 'column',
    });
    const searchInput = createAdminInput('Search Brushes', tileEditorSearchInput, (value) => {
      setTileEditorSearchInput(value);
    });
    tileEditorSearchField = searchInput.querySelector('input');
    const searchLabel = searchInput.querySelector('label');
    if (searchLabel instanceof HTMLElement) {
      searchLabel.remove();
    }
    searchGroup.appendChild(searchInput);
    content.appendChild(searchGroup);

    if (activeTileEditorTab === 'collision') {
      renderCollisionEditor();
      return;
    }
    if (activeTileEditorTab === 'terrain') {
      renderTerrainEditor();
      return;
    }
    if (activeTileEditorTab === 'paths') {
      renderPathEditor();
      return;
    }
    if (activeTileEditorTab === 'npcs') {
      renderNpcEditor();
      return;
    }
    if (activeTileEditorTab === 'portals') {
      renderPortalEditor();
      return;
    }
    renderAssetEditor();
  }

  function renderCollisionEditor(): void {
    const section = createSectionLabel('Collision Brushes');
    section.appendChild(createEditorActionRow({
      onClear: () => setSelectedTool(null),
      onErase: () => setSelectedTool({ kind: 'tile-terrain-erase' }),
      eraseActive: selectedTool?.kind === 'tile-terrain-erase',
    }));
    const collisionBrushes = [
      {
        label: 'Walkable',
        description: 'Floor',
        previewImage: getTerrainTexturePath('floor'),
        active: selectedTool?.kind === 'tile-terrain' && selectedTool.terrain === 'walkable',
        onClick: () => setSelectedTool({ kind: 'tile-terrain', terrain: 'walkable' as const })
      },
      {
        label: 'Path',
        description: 'Speed Boost',
        previewImage: getTerrainTexturePath('cobblestone_path'),
        active: selectedTool?.kind === 'tile-terrain' && selectedTool.terrain === 'path',
        onClick: () => setSelectedTool({ kind: 'tile-terrain', terrain: 'path' as const })
      },
      {
        label: 'Not Walkable',
        description: 'Wall',
        previewStyle: {
          background: 'linear-gradient(135deg, #1f2937 0%, #334155 100%)',
          boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.08)',
        },
        active: selectedTool?.kind === 'tile-terrain' && selectedTool.terrain === 'not_walkable',
        onClick: () => setSelectedTool({ kind: 'tile-terrain', terrain: 'not_walkable' as const })
      },
    ].filter((brush) => matchesTileEditorSearch(brush.label, tileEditorSearchQuery));
    section.appendChild(createBrushTileGrid(
      collisionBrushes
    ));
    appendNoResultsMessage(section, collisionBrushes.length === 0);
    content.appendChild(section);
  }

  function renderTerrainEditor(): void {
    const isSuperAdmin = getIsSuperAdmin();
    const section = createSectionLabel('Terrain Brushes');
    const activeBiomeBrush =
      selectedTool?.kind === 'tile-biome' || selectedTool?.kind === 'tile-biome-region'
        ? selectedTool.biome
        : null;
    const canSetDefaultBiome = isSuperAdmin && activeBiomeBrush !== null;
    section.appendChild(createEditorActionRow({
      onClear: () => setSelectedTool(null),
      onErase: () => setSelectedTool({ kind: 'tile-biome-erase' }),
      eraseActive: selectedTool?.kind === 'tile-biome-erase',
      extraActions: [
        {
          label: selectedTool?.kind === 'tile-biome-region' ? 'Region Active' : 'Biome Region',
          active: selectedTool?.kind === 'tile-biome-region',
          onClick: () => {
            const firstBiome = (TERRAIN_BIOME_DEFINITIONS[0]?.id ?? 'grass') as AdminBiomeBrush;
            setSelectedTool({
              kind: 'tile-biome-region',
              biome:
                selectedTool?.kind === 'tile-biome-region'
                  ? selectedTool.biome
                  : selectedTool?.kind === 'tile-biome'
                    ? selectedTool.biome
                    : firstBiome,
            });
          },
        },
        ...(isSuperAdmin ? [{
          label: isSettingDefaultBiome
            ? 'Setting Default...'
            : canSetDefaultBiome
              ? `Set ${biomeBrushLabel(activeBiomeBrush)} Default`
              : 'Set Brush As Default',
          active: false,
          disabled: !canSetDefaultBiome || isSettingDefaultBiome,
          onClick: () => {
            if (!activeBiomeBrush || isSettingDefaultBiome) {
              return;
            }
            isSettingDefaultBiome = true;
            tileEditorStatus = `Setting default terrain for ${getCurrentMapId()} to ${biomeBrushLabel(activeBiomeBrush)}...`;
            render();
            void onSetMapDefaultBiome({
              mapId: getCurrentMapId(),
              biome: activeBiomeBrush,
            }).then(() => {
              tileEditorStatus = `Set default terrain for ${getCurrentMapId()} to ${biomeBrushLabel(activeBiomeBrush)}.`;
            }).catch((error) => {
              tileEditorStatus = error instanceof Error ? error.message : 'Failed to set default terrain.';
            }).finally(() => {
              isSettingDefaultBiome = false;
              if (isOpen) {
                render();
              }
            });
          },
        }] : []),
      ],
    }));
    const terrainBrushes = [
      {
        label: 'Empty',
        description: 'No Terrain Art',
        previewStyle: {
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(51, 65, 85, 0.72) 100%)',
          boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.08)',
        },
        active: selectedTool?.kind === 'tile-biome-erase',
        onClick: () => setSelectedTool({ kind: 'tile-biome-erase' }),
      },
      ...TERRAIN_BIOME_DEFINITIONS.map((biome) => ({
        label: biome.label,
        previewImage: biome.previewPath,
        active:
          (selectedTool?.kind === 'tile-biome' || selectedTool?.kind === 'tile-biome-region')
          && selectedTool.biome === biome.id,
        onClick: () => setSelectedTool({
          kind: selectedTool?.kind === 'tile-biome-region' ? 'tile-biome-region' : 'tile-biome',
          biome: biome.id as AdminBiomeBrush,
        })
      })),
    ]
      .filter((brush) => matchesTileEditorSearch(brush.label, tileEditorSearchQuery));
    section.appendChild(createBrushTileGrid(
      terrainBrushes
    ));
    appendNoResultsMessage(section, terrainBrushes.length === 0);
    content.appendChild(section);
  }

  function renderPathEditor(): void {
    const currentMapGridShape = getCurrentMapGridShape();
    const currentGridShape = currentMapGridShape === 'hex' || currentMapGridShape === 'triangle'
      ? currentMapGridShape
      : 'square';
    const section = createSectionLabel('Path Brushes');
    section.appendChild(createEditorActionRow({
      onClear: () => setSelectedTool(null),
      onErase: () => setSelectedTool({ kind: 'path-tile-erase', atlasId: activePathAtlasId }),
      eraseActive: selectedTool?.kind === 'path-tile-erase' && selectedTool.atlasId === activePathAtlasId,
    }));

    const paletteTabs = document.createElement('div');
    Object.assign(paletteTabs.style, {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
    });
    const filteredAtlases = PATH_TILE_ATLASES.filter((atlas) => matchesTileEditorSearch(atlas.label, tileEditorSearchQuery));
    filteredAtlases.forEach((atlas) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = atlas.label;
      Object.assign(button.style, buttonStyle(activePathAtlasId === atlas.id ? '#0f766e' : '#334155'));
      button.addEventListener('click', () => {
        activePathAtlasId = atlas.id;
        setSelectedTool({ kind: 'path-tile-autotile', atlasId: atlas.id });
        if (isOpen) {
          render();
        }
      });
      paletteTabs.appendChild(button);
    });
    section.appendChild(paletteTabs);

    const activeAtlas = filteredAtlases.find((atlas) => atlas.id === activePathAtlasId)
      ?? filteredAtlases[0]
      ?? null;
    if (!activeAtlas) {
      appendNoResultsMessage(section, true);
      content.appendChild(section);
      return;
    }
    const resolvedAtlas = getPathTileAtlasDefinition(activeAtlas.id, currentGridShape) ?? activeAtlas;
    const autotileNote = document.createElement('div');
    autotileNote.textContent = `${resolvedAtlas.label} uses ${currentGridShape}-grid autotiling. Dirt and water share the same tile order for each grid shape.`;
    Object.assign(autotileNote.style, {
      color: '#94a3b8',
      fontSize: '12px',
      lineHeight: '1.35',
    });
    section.appendChild(autotileNote);

    const brushCount = getPathTileBrushCount(activeAtlas.id, currentGridShape);
    section.appendChild(createPathBrushTileGrid(
      resolvedAtlas,
      Array.from({ length: brushCount }, (_, tileIndex) => {
        return {
          label: currentGridShape === 'triangle'
            ? getTrianglePathBrushLabel(tileIndex)
            : `${resolvedAtlas.label} Brush ${tileIndex}`,
          previewNode: createPathBrushPreviewNode(
            activeAtlas.id,
            currentGridShape,
            resolvedAtlas.tileSize,
            tileIndex,
            resolvedAtlas.drawScale ?? 1,
          ),
          previewImage: undefined,
          previewStyle: undefined,
          active: selectedTool?.kind === 'path-tile-place'
            && selectedTool.atlasId === activeAtlas.id
            && selectedTool.tileIndex === tileIndex,
          onClick: () => setSelectedTool({ kind: 'path-tile-place', atlasId: resolvedAtlas.id, tileIndex }),
        };
      }),
    ));
    content.appendChild(section);
  }

  function renderNpcEditor(): void {
    const section = createSectionLabel('NPC Brushes');
    section.appendChild(createEditorActionRow({
      onClear: () => setSelectedTool(null),
      onErase: () => setSelectedTool({ kind: 'npc-remove' }),
      eraseActive: selectedTool?.kind === 'npc-remove',
    }));
    const npcBrushes = NPC_TYPES
      .map((npcType) => ({
        label: npcBrushLabel(npcType),
        description: npcType,
        previewText: npcBrushPreview(npcType),
        previewStyle: {
          background: 'rgba(2, 6, 23, 0.55)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          lineHeight: '1',
        },
        active: selectedTool?.kind === 'npc-place' && selectedTool.npcType === npcType,
        onClick: () => setSelectedTool({ kind: 'npc-place', npcType }),
      }))
      .filter((brush) => matchesTileEditorSearch(brush.label, tileEditorSearchQuery));
    section.appendChild(createBrushTileGrid(
      npcBrushes
    ));
    appendNoResultsMessage(section, npcBrushes.length === 0);
    content.appendChild(section);
  }

  function renderAssetEditor(): void {
    const section = createSectionLabel('Asset Brushes');
    section.appendChild(createEditorActionRow({
      onClear: () => setSelectedTool(null),
      onErase: () => setSelectedTool({ kind: 'map-object-remove' }),
      eraseActive: selectedTool?.kind === 'map-object-remove',
    }));
    const uploadButton = document.createElement('button');
    uploadButton.type = 'button';
    uploadButton.textContent = 'Upload Local Asset';
    Object.assign(uploadButton.style, buttonStyle('#475569'));
    uploadButton.addEventListener('click', () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true;
      input.addEventListener('change', () => {
        const files = Array.from(input.files ?? []);
        if (files.length === 0) {
          return;
        }
        void addLocalVisualAssets(files);
      });
      input.click();
    });
    section.appendChild(uploadButton);

    const filteredAssets = getAllVisualAssetDefinitions()
      .filter((asset) => matchesTileEditorSearch(asset.label, tileEditorSearchQuery));
    section.appendChild(createBrushTileGrid(
      filteredAssets.map((asset) => ({
        label: asset.label,
        description: asset.id,
        previewImage: asset.imagePath,
        previewStyle: {
          backgroundSize: 'contain',
          backgroundColor: 'rgba(2, 6, 23, 0.55)',
        },
        active: selectedTool?.kind === 'map-object-place' && selectedTool.assetId === asset.id,
        onClick: () => setSelectedTool({ kind: 'map-object-place', assetId: asset.id }),
        onDragStart: (event) => {
          event.dataTransfer?.setData(VISUAL_ASSET_DRAG_MIME, asset.id);
          if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'copy';
            event.dataTransfer.setDragImage(EMPTY_DRAG_IMAGE, 0, 0);
          }
          window.dispatchEvent(new CustomEvent(MAP_OBJECT_DRAG_STARTED_EVENT, {
            detail: { assetId: asset.id },
          }));
        },
        onDragEnd: () => {
          window.dispatchEvent(new CustomEvent(MAP_OBJECT_DRAG_ENDED_EVENT));
        },
        secondaryAction: localVisualAssets.some((runtimeAsset) => runtimeAsset.id === asset.id)
          ? {
            label: isLocalVisualAssetId(asset.id) ? 'Remove' : 'Reset',
            onClick: () => removeLocalVisualAsset(asset.id),
          }
          : undefined,
      }))
    ));
    appendNoResultsMessage(section, filteredAssets.length === 0);
    content.appendChild(section);
  }

  function renderPortalEditor(): void {
    const section = createSectionLabel('Portal Brushes');
    const portalBrushStatus = document.createElement('div');
    portalBrushStatus.textContent =
      selectedTool?.kind === 'portal-place'
        ? selectedTool.autoPortal === true
          ? `Current brush: auto portal -> ${selectedTool.destMapId}`
          : `Current brush: ${selectedTool.destMapId} -> (${selectedTool.destX}, ${selectedTool.destY})`
        : selectedTool?.kind === 'portal-remove'
          ? 'Current brush: portal eraser'
          : 'Current brush: inactive';
    Object.assign(portalBrushStatus.style, {
      color: '#f8fafc',
      fontSize: '12px',
      padding: '8px',
      borderRadius: '6px',
      background: 'rgba(15, 23, 42, 0.75)',
    });

    if (mapCatalog.length === 0 && !isLoadingMapCatalog) {
      void refreshMapCatalog();
    }

    section.appendChild(portalBrushStatus);
    section.appendChild(createEditorActionRow({
      onClear: () => setSelectedTool(null),
      onErase: () => setSelectedTool({ kind: 'portal-remove' }),
      eraseActive: selectedTool?.kind === 'portal-remove',
    }));
    section.appendChild(createMapSelect(portalDestMapId, (value) => { portalDestMapId = value; }, mapCatalog));

    const autoPortalLabel = document.createElement('label');
    Object.assign(autoPortalLabel.style, {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#e2e8f0',
      fontSize: '12px',
      marginTop: '8px',
    });
    const autoPortalInput = document.createElement('input');
    autoPortalInput.type = 'checkbox';
    autoPortalInput.checked = portalAutoMode;
    autoPortalInput.addEventListener('change', () => {
      portalAutoMode = autoPortalInput.checked;
      render();
    });
    autoPortalLabel.appendChild(autoPortalInput);
    autoPortalLabel.appendChild(document.createTextNode('Auto-portal'));
    section.appendChild(autoPortalLabel);

    const portalCoordsRow = document.createElement('div');
    Object.assign(portalCoordsRow.style, { display: 'flex', gap: '8px' });
    portalCoordsRow.appendChild(createAdminInput('Dest X', portalDestX, (value) => { portalDestX = value; }, 'number'));
    portalCoordsRow.appendChild(createAdminInput('Dest Y', portalDestY, (value) => { portalDestY = value; }, 'number'));
    portalCoordsRow.style.opacity = portalAutoMode ? '0.55' : '1';
    portalCoordsRow.style.pointerEvents = portalAutoMode ? 'none' : 'auto';
    section.appendChild(portalCoordsRow);

    const armPortalBtn = document.createElement('button');
    armPortalBtn.type = 'button';
    armPortalBtn.textContent = selectedTool?.kind === 'portal-place' ? 'Portal Brush Armed' : 'Arm Portal Brush';
    Object.assign(armPortalBtn.style, buttonStyle('#7c3aed'));
    armPortalBtn.addEventListener('click', () => {
      if (portalAutoMode) {
        if (!portalDestMapId) {
          tileEditorStatus = 'Portal destination map is required.';
          render();
          return;
        }
        setSelectedTool({ kind: 'portal-place', destMapId: portalDestMapId, destX: 0, destY: 0, autoPortal: true });
        return;
      }

      const destX = Number(portalDestX);
      const destY = Number(portalDestY);
      if (!portalDestMapId || !Number.isFinite(destX) || !Number.isFinite(destY)) {
        tileEditorStatus = 'Portal destination map and coordinates are required.';
        render();
        return;
      }
      setSelectedTool({ kind: 'portal-place', destMapId: portalDestMapId, destX, destY, autoPortal: false });
    });
    section.appendChild(armPortalBtn);
    content.appendChild(section);
  }

  function updateTileEditorStatusForTool(tool: AdminToolSelection | null): void {
    if (!tool) {
      tileEditorStatus = 'No admin brush selected.';
      return;
    }
    if (tool.kind === 'tile-terrain') {
      tileEditorStatus = `Active collision brush: ${terrainBrushLabel(tool.terrain)}`;
      return;
    }
    if (tool.kind === 'tile-terrain-erase') {
      tileEditorStatus = 'Collision eraser active.';
      return;
    }
    if (tool.kind === 'tile-biome') {
      tileEditorStatus = `Active terrain brush: ${biomeBrushLabel(tool.biome)}`;
      return;
    }
    if (tool.kind === 'tile-biome-region') {
      tileEditorStatus = `Active biome region brush: ${biomeBrushLabel(tool.biome)}. Click and drag to paint a rectangle.`;
      return;
    }
    if (tool.kind === 'tile-biome-erase') {
      tileEditorStatus = 'Terrain eraser active.';
      return;
    }
    if (tool.kind === 'path-tile-autotile') {
      const gridShape = getCurrentMapGridShape() === 'hex' ? 'hex' : 'square';
      tileEditorStatus = `Active path brush: ${tool.atlasId} ${gridShape} autotile`;
      return;
    }
    if (tool.kind === 'path-tile-place') {
      tileEditorStatus = `Active fixed path brush: ${tool.atlasId} tile ${tool.tileIndex}`;
      return;
    }
    if (tool.kind === 'path-tile-erase') {
      tileEditorStatus = `Path eraser active: ${tool.atlasId}`;
      return;
    }
    if (tool.kind === 'npc-place') {
      tileEditorStatus = `Active NPC brush: ${tool.npcType}`;
      return;
    }
    if (tool.kind === 'npc-remove') {
      tileEditorStatus = 'NPC eraser active.';
      return;
    }
    if (tool.kind === 'map-object-place') {
      tileEditorStatus = `Active asset brush: ${visualAssetBrushLabel(tool.assetId)}`;
      return;
    }
    if (tool.kind === 'map-object-remove') {
      tileEditorStatus = 'Asset eraser active.';
      return;
    }
    if (tool.kind === 'portal-remove') {
      tileEditorStatus = 'Portal eraser active.';
      return;
    }
    tileEditorStatus = tool.autoPortal === true
      ? `Active auto portal brush: ${tool.destMapId}`
      : `Active portal brush: ${tool.destMapId} -> (${tool.destX}, ${tool.destY})`;
  }

  function findExistingAssetForUpload(
    fileName: string,
    definitions: readonly ReturnType<typeof getAllVisualAssetDefinitions>[number][],
  ): ReturnType<typeof getVisualAssetDefinition> {
    const normalizedFileName = fileName.trim().toLowerCase();
    if (!normalizedFileName) {
      return null;
    }
    const baseName = normalizedFileName.replace(/\.[^.]+$/, '');
    return definitions.find((definition) => {
      const imageName = definition.imagePath.split('/').pop()?.toLowerCase() ?? '';
      const definitionId = definition.id.toLowerCase();
      return imageName === normalizedFileName || definitionId === baseName;
    }) ?? null;
  }

  function summarizeAssetUpload(createdCount: number, replacedCount: number): string {
    const parts: string[] = [];
    if (replacedCount > 0) {
      parts.push(`updated ${replacedCount} existing asset${replacedCount === 1 ? '' : 's'}`);
    }
    if (createdCount > 0) {
      parts.push(`uploaded ${createdCount} new local asset${createdCount === 1 ? '' : 's'}`);
    }
    if (parts.length === 0) {
      return 'No assets were uploaded.';
    }
    const [first, second] = parts;
    return `${first.charAt(0).toUpperCase()}${first.slice(1)}${second ? ` and ${second}` : ''}.`;
  }

  async function refreshMapCatalog(): Promise<void> {
    if (isLoadingMapCatalog) return;
    try {
      isLoadingMapCatalog = true;
      const maps = await loadMapCatalog();
      mapCatalog = maps;
      if (!deleteMapId && maps.length > 0) {
        deleteMapId = maps[0].mapId;
      }
      if (!portalDestMapId && maps.length > 0) {
        portalDestMapId = maps[0].mapId;
      }
      mapCatalogStatus = maps.length > 0 ? `Loaded ${maps.length} map definition(s).` : 'No maps available.';
    } catch (error) {
      mapCatalogStatus = error instanceof Error ? error.message : 'Failed to load map catalog.';
    } finally {
      isLoadingMapCatalog = false;
      if (isOpen) {
        render();
      }
    }
  }

  return {
    show,
    hide,
    openTab(tab: AdminTab): void {
      setActiveTab(tab);
      show();
    },
    isOpenOnTab(tab: AdminTab): boolean {
      return isOpen && activeTab === tab;
    },
    refresh(): void {
      if (isOpen) {
        render();
      }
    },
    destroy(): void {
      if (tileEditorSearchTimeoutId !== null) {
        window.clearTimeout(tileEditorSearchTimeoutId);
        tileEditorSearchTimeoutId = null;
      }
      windowFrame.destroy();
      container.remove();
    },
  };
}

function matchesTileEditorSearch(label: string, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return true;
  }
  return label.toLowerCase().includes(normalizedQuery);
}

function appendNoResultsMessage(section: HTMLElement, isEmpty: boolean): void {
  if (!isEmpty) {
    return;
  }
  const message = document.createElement('div');
  message.textContent = 'No brushes match the current search.';
  Object.assign(message.style, {
    color: '#94a3b8',
    fontSize: '12px',
    padding: '8px 0 0',
  });
  section.appendChild(message);
}

function createTileEditorSubtabBar(
  activeTab: AdminTileEditorTab,
  onChange: (tab: AdminTileEditorTab) => void
): HTMLDivElement {
  const bar = document.createElement('div');
  Object.assign(bar.style, {
    display: 'grid',
    gridTemplateColumns: `repeat(${TILE_EDITOR_TABS.length}, minmax(0, 1fr))`,
    gap: '8px',
  });

  for (const tab of TILE_EDITOR_TABS) {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = TILE_EDITOR_TAB_LABELS[tab];
    const selected = tab === activeTab;
    Object.assign(button.style, {
      padding: '10px 12px',
      borderRadius: '8px',
      border: selected ? '1px solid rgba(56, 189, 248, 0.75)' : '1px solid rgba(148, 163, 184, 0.2)',
      background: selected ? 'rgba(56, 189, 248, 0.14)' : 'rgba(15, 23, 42, 0.75)',
      color: selected ? '#f8fafc' : '#cbd5e1',
      cursor: 'pointer',
      fontWeight: '600',
    });
    button.addEventListener('click', () => onChange(tab));
    bar.appendChild(button);
  }

  return bar;
}

function createEditorActionRow(options: {
  onClear: () => void;
  onErase: () => void;
  eraseActive: boolean;
  extraAction?: {
    label: string;
    active: boolean;
    onClick: () => void;
  };
  extraActions?: Array<{
    label: string;
    active: boolean;
    disabled?: boolean;
    onClick: () => void;
  }>;
}): HTMLDivElement {
  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    gap: '8px',
  });

  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.textContent = t('admin.clearTool');
  Object.assign(clearBtn.style, buttonStyle('#475569'));
  Object.assign(clearBtn.style, { flex: '1' });
  clearBtn.addEventListener('click', options.onClear);

  const eraseBtn = document.createElement('button');
  eraseBtn.type = 'button';
  eraseBtn.textContent = options.eraseActive ? 'Eraser Active' : 'Eraser';
  Object.assign(eraseBtn.style, buttonStyle(options.eraseActive ? '#b91c1c' : '#7f1d1d'));
  Object.assign(eraseBtn.style, { flex: '1' });
  eraseBtn.addEventListener('click', options.onErase);

  row.appendChild(clearBtn);
  row.appendChild(eraseBtn);
  const extraActions = options.extraActions ?? (options.extraAction ? [options.extraAction] : []);
  for (const action of extraActions) {
    const extraBtn = document.createElement('button');
    extraBtn.type = 'button';
    extraBtn.textContent = action.label;
    extraBtn.disabled = action.disabled === true;
    Object.assign(extraBtn.style, buttonStyle(action.active ? '#0f766e' : '#1d4ed8'));
    Object.assign(extraBtn.style, { flex: '1' });
    if (action.disabled === true) {
      Object.assign(extraBtn.style, {
        opacity: '0.55',
        cursor: 'not-allowed',
      });
    } else {
      extraBtn.addEventListener('click', action.onClick);
    }
    row.appendChild(extraBtn);
  }
  return row;
}

function createBrushTileGrid(
  items: Array<{
    label: string;
    description?: string;
    previewImage?: string;
    previewText?: string;
    previewStyle?: Partial<CSSStyleDeclaration>;
    active: boolean;
    onClick: () => void;
    onDragStart?: (event: DragEvent) => void;
    onDragEnd?: () => void;
    secondaryAction?: {
      label: string;
      onClick: () => void;
    };
  }>
): HTMLDivElement {
  const grid = document.createElement('div');
  Object.assign(grid.style, {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
    gap: '8px',
  });

  for (const item of items) {
    const button = document.createElement('button');
    button.type = 'button';
    Object.assign(button.style, {
      minHeight: '76px',
      padding: '10px 8px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '4px',
      borderRadius: '8px',
      border: item.active ? '1px solid rgba(56, 189, 248, 0.85)' : '1px solid rgba(148, 163, 184, 0.22)',
      background: item.active ? 'rgba(56, 189, 248, 0.16)' : 'rgba(15, 23, 42, 0.78)',
      color: item.active ? '#f8fafc' : '#cbd5e1',
      cursor: 'pointer',
      textAlign: 'center',
    });
    button.addEventListener('click', item.onClick);
    if (item.onDragStart) {
      button.draggable = true;
      button.addEventListener('dragstart', item.onDragStart);
      if (item.onDragEnd) {
        button.addEventListener('dragend', item.onDragEnd);
      }
    }

    if (item.previewImage) {
      const preview = document.createElement('div');
      Object.assign(preview.style, {
        width: '100%',
        height: '44px',
        borderRadius: '6px',
        backgroundImage: `url(${item.previewImage})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: item.active ? '0 0 0 1px rgba(255, 255, 255, 0.1) inset' : 'none',
      });
      if (item.previewStyle) {
        Object.assign(preview.style, item.previewStyle);
      }
      button.appendChild(preview);
    } else if (item.previewText) {
      const preview = document.createElement('div');
      preview.textContent = item.previewText;
      Object.assign(preview.style, {
        width: '100%',
        height: '44px',
        borderRadius: '6px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: item.active ? '0 0 0 1px rgba(255, 255, 255, 0.1) inset' : 'none',
      });
      if (item.previewStyle) {
        Object.assign(preview.style, item.previewStyle);
      }
      button.appendChild(preview);
    } else if (item.previewStyle) {
      const preview = document.createElement('div');
      Object.assign(preview.style, {
        width: '100%',
        height: '44px',
        borderRadius: '6px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: item.active ? '0 0 0 1px rgba(255, 255, 255, 0.1) inset' : 'none',
      });
      Object.assign(preview.style, item.previewStyle);
      button.appendChild(preview);
    }

    const label = document.createElement('div');
    label.textContent = item.label;
    Object.assign(label.style, {
      fontSize: '13px',
      fontWeight: '600',
      lineHeight: '1.2',
    });
    button.appendChild(label);

    if (item.description) {
      const description = document.createElement('div');
      description.textContent = item.description;
      Object.assign(description.style, {
        fontSize: '11px',
        color: '#94a3b8',
        lineHeight: '1.2',
        wordBreak: 'break-word',
      });
      button.appendChild(description);
    }

    if (item.secondaryAction) {
      const actionButton = document.createElement('button');
      actionButton.type = 'button';
      actionButton.textContent = item.secondaryAction.label;
      Object.assign(actionButton.style, {
        marginTop: '4px',
        padding: '4px 8px',
        borderRadius: '6px',
        border: '1px solid rgba(248, 113, 113, 0.35)',
        background: 'rgba(127, 29, 29, 0.2)',
        color: '#fecaca',
        cursor: 'pointer',
        fontSize: '11px',
      });
      actionButton.addEventListener('click', (event) => {
        event.stopPropagation();
        item.secondaryAction?.onClick();
      });
      button.appendChild(actionButton);
    }

    grid.appendChild(button);
  }

  return grid;
}

function createPathBrushTileGrid(
  atlas: { columns: number; tileSize: number },
  items: Array<{
    label?: string;
    previewImage?: string;
    previewNode?: HTMLElement | null;
    previewStyle?: Partial<CSSStyleDeclaration>;
    active: boolean;
    onClick: () => void;
  }>
): HTMLDivElement {
  const previewSizePx = Math.max(32, Math.min(48, Math.round(atlas.tileSize * 0.6875)));
  const grid = document.createElement('div');
  Object.assign(grid.style, {
    display: 'grid',
    gridTemplateColumns: `repeat(${atlas.columns}, ${previewSizePx}px)`,
    gap: '0',
    alignSelf: 'start',
  });

  for (const item of items) {
    const button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('aria-pressed', item.active ? 'true' : 'false');
    if (item.label) {
      button.title = item.label;
      button.setAttribute('aria-label', item.label);
    }
    Object.assign(button.style, {
      width: `${previewSizePx}px`,
      height: `${previewSizePx}px`,
      padding: '0',
      margin: '0',
      border: 'none',
      borderRadius: '0',
      background: 'transparent',
      cursor: 'pointer',
      overflow: 'hidden',
      appearance: 'none',
    });
    button.addEventListener('click', item.onClick);

    const preview = document.createElement('div');
    Object.assign(preview.style, {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      imageRendering: 'pixelated',
      filter: item.active ? 'brightness(1.18) saturate(1.15)' : 'none',
      transform: item.active ? 'scale(0.94)' : 'scale(1)',
      transformOrigin: 'center',
      transition: 'filter 120ms ease, transform 120ms ease',
    });
    if (item.previewImage) {
      preview.style.backgroundImage = `url(${item.previewImage})`;
      preview.style.backgroundPosition = 'center';
      preview.style.backgroundSize = 'cover';
      preview.style.backgroundRepeat = 'no-repeat';
    }
    if (item.previewStyle) {
      Object.assign(preview.style, item.previewStyle);
    }
    if (item.previewNode) {
      Object.assign(item.previewNode.style, {
        width: '100%',
        height: '100%',
        display: 'block',
      });
      preview.appendChild(item.previewNode);
    }

    button.appendChild(preview);
    grid.appendChild(button);
  }

  return grid;
}

function terrainBrushLabel(brush: AdminTerrainBrush): string {
  switch (brush) {
    case 'walkable':
      return 'Walkable';
    case 'path':
      return 'Path';
    case 'not_walkable':
      return 'Not Walkable';
  }
}

function biomeBrushLabel(brush: AdminBiomeBrush): string {
  return getTerrainBiomeDefinition(brush)?.label ?? brush;
}

function npcBrushLabel(brush: string): string {
  switch (brush) {
    case 'merchant':
      return t('npcs.palette.merchant');
    case 'guard':
      return t('npcs.palette.guard');
    case 'villager':
      return t('npcs.palette.villager');
    case 'town_wall_v':
      return t('npcs.palette.wallVert');
    case 'town_wall_h':
      return t('npcs.palette.wallHoriz');
    case 'town_gate':
      return t('npcs.palette.townGate');
    case 'shrine':
      return t('npcs.palette.shrine');
    default:
      return brush;
  }
}

function npcBrushPreview(brush: string): string {
  switch (brush) {
    case 'merchant':
      return '🏪';
    case 'guard':
      return '🛡️';
    case 'villager':
      return '👨';
    case 'town_wall_v':
    case 'town_wall_h':
      return '🧱';
    case 'town_gate':
      return '🚪';
    case 'shrine':
      return '⛩️';
    default:
      return '👤';
  }
}

function visualAssetBrushLabel(assetId: string): string {
  return getVisualAssetDefinition(assetId)?.label ?? assetId;
}
